import React, { useEffect, useState } from 'react';
import { Block, NativeFormBlockContent } from '../types';
import { Nullable } from 'src/types/nullable.type';
import { NeueJourneyButton } from '../components/neue-button/journey';
import { animated, easings, useTransition } from '@react-spring/web';
import { FLOAT_ANIMATION_CONFIG } from 'src/document-editor/neue-bubble-menu-fixed';
import { NativeFormElementContentPanel } from './element-content-panel';
import {
  NativeFormConfig,
  NativeFormElement,
  NativeFormElementSpecs,
  NativeFormElementType,
  useNativeFormStore,
} from './store';
import { NativeFormElementInitialValuesFactory, NativeFormElementPlaceholderFactory } from './element.factory';
import { shallow } from 'zustand/shallow';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { DragHandleSmallIcon, MinusRegularIcon, SettingsSlidersStrongIcon } from 'src/monet/icons';
import { useDraggableInPortal } from '../mutual-action-plans/hooks/use-draggable-in-portal.hook';
import classNames from 'classnames';
import { useEditorStore } from '../editor-store';
import { v4 as uuidv4 } from 'uuid';
import { useUpdateEffect } from 'react-use';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/outline';
import { useEditorContext } from 'src/store/editor';
import { Journey } from 'src/common/interfaces/journey.interface';

export type NativeFormElementPanelType = 'add' | 'config' | 'block';

const MAX_ITEM_SIZE = 8;

export const AddNativeFormPanel = ({ block }: { block: Block }) => {
  const [activePanel, setActivePanel] = useState<Nullable<NativeFormElementPanelType>>(null);
  const [formElementProps, setFormElementProps] = useState<Nullable<NativeFormElement>>(null);

  const inputs = useNativeFormStore((state) => state.inputs, shallow);
  const setInputElement = useNativeFormStore((state) => state.setInputElement);
  const replaceInputs = useNativeFormStore((state) => state.replaceInputs);
  const resetInputs = useNativeFormStore((state) => state.reset);
  const activeJourneyAlias = useEditorContext((state) => {
    const { aliases, slug } = state.journey as Journey;
    return aliases.find((alias) => alias.slug === slug) || null;
  }, shallow);
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);

  const data = activePanel ? [activePanel] : [];
  const blockContent = block.content as NativeFormBlockContent;

  const [transitions, formElementPickerTransitionApi] = useTransition<
    NativeFormElementPanelType,
    { y: number; opacity: number }
  >(data, () => ({
    key: (type: NativeFormElementPanelType) => type,
    from: () => {
      return {
        y: 8,
        opacity: 0,
      };
    },
    enter: () => {
      return {
        y: 0,
        opacity: 1,
        config: FLOAT_ANIMATION_CONFIG,
      };
    },
    leave: () => {
      return {
        y: 0,
        opacity: 0,
        config: { duration: 200, easing: easings.easeOutCubic },
      };
    },
  }));

  useUpdateEffect(() => {
    if (!formElementProps) {
      return;
    }
    const blockFieldFromProps = blockContent.fields.find((field) => field.id === formElementProps.id);
    if (!blockFieldFromProps) {
      return;
    }

    if (blockFieldFromProps.id === formElementProps.id) {
      setFormElementProps(blockFieldFromProps);
    }
  }, [formElementProps, blockContent.fields]);

  useEffect(() => {
    if (block.content.type === 'placeholder') {
      resetInputs();
      return;
    }
    if (!blockContent.fields) {
      return;
    }
    blockContent.fields.forEach((field) => {
      setInputElement(field.id, field);
    });
  }, [blockContent, block.content]);

  useEffect(() => {
    formElementPickerTransitionApi.start();
  }, [activePanel]);

  const dispatchNativeFormElements = (fields: NativeFormElement[]) => {
    if (!activeJourneyAlias) {
      return;
    }
    dispatchUserEditorAction(
      {
        type: 'update-block-content',
        content: { type: 'native-form', config: blockContent.config, fields, formUUID: activeJourneyAlias.uuid },
        id: block.id,
      },
      {
        syncable: true,
        undoable: false,
      }
    );
  };

  const dispatchNativeFormConfig = (config: NativeFormConfig) => {
    dispatchUserEditorAction({
      type: 'update-block-content',
      content: { type: 'native-form', config, fields: blockContent?.fields || [] },
      id: block.id,
    });
  };

  const createFormElement = (element: NativeFormElement, specs: NativeFormElementSpecs) => {
    const existingFields = blockContent.fields || [];
    dispatchNativeFormElements([...existingFields, { ...element, ...specs }]);
  };

  const onNewFormElementAdded = (type: NativeFormElementType) => {
    const uuid = uuidv4().replace(/-/g, '');
    const newItemPosition = blockContent.fields?.length || 0;

    const label = `${type} ${newItemPosition + 1}`;

    const elementProps = NativeFormElementInitialValuesFactory(type, label, {
      uuid,
      position: newItemPosition,
    });
    createFormElement(elementProps, { uuid, position: newItemPosition });
    setFormElementProps(elementProps);
    setActivePanel('config');
  };

  const onAddElementClicked = () => {
    setActivePanel('add');
  };

  const onSettingsClicked = () => {
    if (activePanel === 'block') {
      setActivePanel(null);
      return;
    }

    setActivePanel('block');
  };

  const sortedInputElements = Object.values(inputs).sort((a, b) => a.position - b.position);

  const onReorderingFormElementsEnd = (result: DropResult) => {
    if (!blockContent.fields) {
      return;
    }

    const { source, destination } = result;

    if (!source || !destination) {
      return;
    }

    const { index: sourceIndex, droppableId: sourceDroppableId } = source;
    const { index: destinationIndex, droppableId: destinationDroppableId } = destination;

    if (!sourceDroppableId || !destinationDroppableId) return;
    if (sourceDroppableId !== destinationDroppableId) return;
    if (sourceIndex === destinationIndex) return;

    const sourceBlockField = blockContent.fields.find((field) => field.position === sourceIndex);
    const destinationBlockField = blockContent.fields.find((field) => field.position === destinationIndex);

    if (!sourceBlockField || !destinationBlockField) {
      return;
    }

    const notChangedBlockFields = blockContent.fields.filter(
      (field) => ![sourceBlockField, destinationBlockField].includes(field)
    );

    dispatchNativeFormElements([
      ...notChangedBlockFields,
      { ...sourceBlockField, position: destinationIndex },
      { ...destinationBlockField, position: sourceIndex },
    ]);
  };

  const inputElements = sortedInputElements.map((input) => ({
    type: input.type,
    label: input.label,
    isRequired: input.required,
  }));
  const renderDraggable = useDraggableInPortal({ classes: 'neue-native-form' });

  const onRemoveNativeFormElement = (index: number) => () => {
    if (!blockContent.fields) {
      return;
    }
    const filteredFormElements = blockContent.fields.filter((field) => field.position !== index);
    dispatchNativeFormElements(filteredFormElements);

    const filteredInputs = filteredFormElements.reduce(
      (acc, field) => ({
        ...acc,
        [field.id]: field,
      }),
      {} as Record<string, NativeFormElement>
    );

    replaceInputs(filteredInputs);
  };

  const onClickNativeFormElement = (index: number) => () => {
    const fieldToUpdate = blockContent.fields.find((field) => field.position === index) || null;
    if (!fieldToUpdate) {
      return;
    }
    setFormElementProps(fieldToUpdate);
    setActivePanel('config');
  };

  const isConfigCheckbox = formElementProps && formElementProps.type === 'checkbox' && activePanel === 'config';

  return (
    <>
      {transitions((style) => (
        <animated.div
          className={classNames('absolute top-0 right-[255px] mr-2', {
            'max-h-[40vh] overflow-x-hidden overflow-y-auto': isConfigCheckbox,
          })}
          style={style}
        >
          <NativeFormElementContentPanel
            type={activePanel}
            block={block}
            formElement={formElementProps}
            onAddClick={onNewFormElementAdded}
            onUpdateField={dispatchNativeFormElements}
            onUpdateConfig={dispatchNativeFormConfig}
          />
        </animated.div>
      ))}
      <div className='flex flex-col gap-4'>
        <span className='text-neue-journey-small -mt-3'>Add native form</span>
        <DragDropContext onDragEnd={onReorderingFormElementsEnd}>
          {inputElements.length > 0 && (
            <Droppable droppableId='native-form-elements' type='native-form-elements'>
              {(provided, snapshot) => {
                return (
                  <div
                    data-is-dragging-over={snapshot.isDraggingOver}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className='flex flex-col gap-2 text-neue-journey-fg'
                  >
                    {inputElements.map((input, index) => {
                      const NativeFormPlaceholderElement = NativeFormElementPlaceholderFactory(input.type);
                      const RequiredIcon = input.isRequired ? LockClosedIcon : LockOpenIcon;
                      return (
                        <Draggable
                          key={`${input.type}-${index}`}
                          draggableId={`${input.type}-${index}`}
                          index={index}
                          isDragDisabled={inputElements.length === 1}
                        >
                          {renderDraggable((provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={provided.draggableProps.style}
                              data-is-dragging={snapshot.isDragging}
                              className={classNames(
                                'group relative flex gap-2 rounded-lg before:transition-colors after:transition-colors',
                                {
                                  'before:content-[""] after:z-[-1] before:absolute before:-inset-2 before:bg-neue-canvas-fg-10 before:rounded-lg':
                                    snapshot.isDragging,
                                  'after:content-[""] after:z-[-2] after:absolute after:-inset-2 after:text-neue-journey-fg after:rounded-lg':
                                    snapshot.isDragging,
                                }
                              )}
                            >
                              <div {...provided.dragHandleProps} className='flex items-center flex-shrink-0'>
                                <DragHandleSmallIcon />
                              </div>
                              <div className='flex items-center gap-2' onClick={onClickNativeFormElement(index)}>
                                <NativeFormPlaceholderElement label={input.label} />
                                <RequiredIcon className='w-4 h-4 text-neue-journey-fg shrink-0' />
                              </div>
                              <div
                                className={classNames(
                                  'flex-shrink-0 opacity-0 text-neue-journey-bg rounded-full bg-neue-journey-fg-10',
                                  'group-hover:opacity-100 transition-opacity cursor-pointer h-4 w-4 m-auto'
                                )}
                                onClick={onRemoveNativeFormElement(index)}
                              >
                                <MinusRegularIcon />
                              </div>
                            </div>
                          ))}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          )}
        </DragDropContext>
        <div className='flex gap-2'>
          <NeueJourneyButton className='w-10 h-10' variant='secondary' onClick={onSettingsClicked}>
            <SettingsSlidersStrongIcon className='w-4 h-4' />
          </NeueJourneyButton>
          <NeueJourneyButton
            onClick={onAddElementClicked}
            disabled={inputElements.length === MAX_ITEM_SIZE}
            className='h-10 w-full'
          >
            Add form element
          </NeueJourneyButton>
        </div>
      </div>
    </>
  );
};
